/* eslint-disable */
import React, { useEffect, useState } from 'react';
import Modal from '../../../../shared/modal';
import { useAppDispatch } from '../../../../app/hooks';
import {
    tryToFetchHodReport,
    tryToSaveHodReport,
} from '../../../../store/admin/journey-reports/journeyReportsSlice';

interface ISection {
    cabinId: string;
    aiSummary: string;
    editedSummary: string;
}

interface IHodReportModalProps {
    open: boolean;
    tripId: string;
    reportType: 'cabin' | 'bar' | 'restaurant';
    onClose: () => void;
    onSaved?: () => void;
}

const TYPE_LABELS: Record<string, string> = {
    cabin: 'Cabin Report',
    bar: 'Bar Report',
    restaurant: 'Restaurant Report',
};

const HodReportModal: React.FC<IHodReportModalProps> = ({
    open,
    tripId,
    reportType,
    onClose,
    onSaved,
}) => {
    const dispatch = useAppDispatch();
    const [sections, setSections] = useState<ISection[]>([]);
    const [submittedAt, setSubmittedAt] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /* ── Load report on open ── */
    useEffect(() => {
        if (!open || !tripId || !reportType) return;
        setIsLoading(true);
        setError(null);
        dispatch(tryToFetchHodReport({ tripId, type: reportType }))
            .unwrap()
            .then((data: any) => {
                const secs: ISection[] = (data?.sections || []).map((s: any) => ({
                    cabinId: s.cabinId || '',
                    aiSummary: s.aiSummary || '',
                    editedSummary: s.editedSummary ?? s.aiSummary ?? '',
                }));
                setSections(secs);
                setSubmittedAt(data?.submittedAt || null);
            })
            .catch((err: any) => {
                setError(typeof err === 'string' ? err : 'Failed to load report');
            })
            .finally(() => setIsLoading(false));
    }, [open, tripId, reportType]);

    /* ── Reset on close ── */
    useEffect(() => {
        if (!open) {
            setSections([]);
            setSubmittedAt(null);
            setError(null);
        }
    }, [open]);

    /* ── Handlers ── */
    const handleSectionChange = (idx: number, value: string) => {
        setSections((prev) =>
            prev.map((s, i) => (i === idx ? { ...s, editedSummary: value } : s)),
        );
    };

    const handleResetToAi = (idx: number) => {
        setSections((prev) =>
            prev.map((s, i) => (i === idx ? { ...s, editedSummary: s.aiSummary } : s)),
        );
    };

    const handleSaveSubmit = async () => {
        if (isSaving) return;
        setIsSaving(true);
        setError(null);
        try {
            await dispatch(
                tryToSaveHodReport({
                    tripId,
                    type: reportType,
                    sections: sections.map((s) => ({
                        cabinId: s.cabinId,
                        editedSummary: s.editedSummary,
                    })),
                }),
            ).unwrap();
            onSaved?.();
            onClose();
        } catch (err: any) {
            setError(typeof err === 'string' ? err : 'Failed to save report');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            contentContainerStyle='max-w-[95%] w-[700px] rounded-lg mb-4'
            title={TYPE_LABELS[reportType] || 'HOD Report'}
        >
            <div className='font-inter'>
                {/* Status badge */}
                {submittedAt && (
                    <div className='mb-4 flex items-center gap-2'>
                        <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                            <span className='w-1.5 h-1.5 rounded-full bg-green-500' />
                            Submitted
                        </span>
                        <span className='text-xs text-gray-500'>
                            {new Date(submittedAt).toLocaleString('en-GB', { timeZone: 'Europe/Rome' })}
                        </span>
                    </div>
                )}

                {/* Loading */}
                {isLoading && (
                    <div className='flex items-center justify-center py-12'>
                        <div className='w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin' />
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className='mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm'>
                        {error}
                    </div>
                )}

                {/* Sections */}
                {!isLoading && sections.length > 0 && (
                    <div className='space-y-6 max-h-[60vh] overflow-y-auto pr-1'>
                        {sections.map((section, idx) => (
                            <div
                                key={section.cabinId + idx}
                                className='border border-[#E9EAEB] rounded-lg p-4'
                            >
                                <div className='flex items-center justify-between mb-2'>
                                    <h4 className='text-sm font-semibold text-gray-800'>
                                        {section.cabinId || `Section ${idx + 1}`}
                                    </h4>
                                    <button
                                        onClick={() => handleResetToAi(idx)}
                                        disabled={
                                            section.editedSummary === section.aiSummary || isSaving
                                        }
                                        className='text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition'
                                    >
                                        Reset to AI version
                                    </button>
                                </div>
                                <textarea
                                    rows={6}
                                    value={section.editedSummary}
                                    onChange={(e) =>
                                        handleSectionChange(idx, e.target.value)
                                    }
                                    disabled={isSaving}
                                    className='w-full border border-[#E9EAEB] rounded-lg p-3 text-sm text-gray-800 resize-y focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50'
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && !error && sections.length === 0 && (
                    <div className='text-center py-12 text-gray-400 text-sm'>
                        No sections available for this report.
                    </div>
                )}

                {/* Footer actions */}
                {!isLoading && sections.length > 0 && (
                    <div className='flex justify-end gap-3 mt-6 pt-4 border-t border-[#E9EAEB]'>
                        <button
                            onClick={onClose}
                            className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition'
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveSubmit}
                            disabled={isSaving}
                            className='px-5 py-2 text-sm font-medium text-white bg-[#2D8EFF] rounded-lg hover:bg-[#2578DB] transition disabled:opacity-50 flex items-center gap-2'
                        >
                            {isSaving && (
                                <div className='w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin' />
                            )}
                            Save &amp; Submit
                        </button>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default HodReportModal;
